import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {Button} from "../basic/button.tsx";
import {Input} from "../basic/input.tsx";
import {Label} from "../basic/label.tsx";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";
import {VwpTeam} from "../../store/generated/api";
import {useAuth} from "../../hooks/useAuth";

interface TeamInformationFormProps {
    team?: VwpTeam;
    isLoading: boolean;
    onValidChange: (valid: boolean) => void;
    onSubmit: (data: {
        firstName: string;
        lastName: string;
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        vatNumber?: string;
        quantity: number;
    }) => void;
    isSubmitting: boolean;
    submitButtonText?: string;
    initialQuantity?: number;
    isTrial?: boolean;
    showSubmitButton?: boolean;
    requireVat?: boolean;
}

export function TeamInformationForm({
                                        team,
                                        isLoading,
                                        onValidChange,
                                        onSubmit,
                                        isSubmitting,
                                        submitButtonText,
                                        initialQuantity = 1,
                                        isTrial = false,
                                         showSubmitButton = true,
                                         requireVat = false
                                    }: TeamInformationFormProps) {
    const {t} = useTranslationWithKeys();
    const {user} = useAuth();
    const [quantity, setQuantity] = useState(initialQuantity);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: {errors, isValid}
    } = useForm<{
        firstName: string;
        lastName: string;
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        vatNumber?: string;
    }>({
        mode: "onChange"
    });

    const handleFormSubmit = (data: {
        firstName: string;
        lastName: string;
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        vatNumber?: string;
    }) => {
        onSubmit({...data, quantity});
    };

    useEffect(() => {
        if (team || user) {
            reset({
                firstName: user?.firstName || "",
                lastName: user?.lastName || "",
                teamName: team?.name || "",
                streetAddress: team?.streetAddress || "",
                city: team?.city || "",
                stateProvince: team?.stateProvince || "",
                postalCode: team?.postalCode || "",
                country: team?.country || "",
                phoneNumber: team?.phoneNumber || "",
                billingEmail: team?.billingEmail || "",
                vatNumber: team?.vatNumber || ""
            });
        }
    }, [team, user, reset]);

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    return (
        <>
            <form id="team-information-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                {isTrial && (
                    <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                        <p className="text-sm text-muted-foreground">
                            {t('trial.defaultQuantityMessage')}
                        </p>
                        <div className="flex items-center gap-4">
                            <Label htmlFor="quantity">{t('pricing.quantity')}</Label>
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    disabled={quantity <= 1}
                                >
                                    -
                                </Button>
                                <span className="w-12 text-center font-medium">{quantity}</span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setQuantity(quantity + 1)}
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="firstName">{t('settings.firstName')} *</Label>
                        <Input
                            id="firstName"
                            {...register("firstName", {required: t('settings.firstNameRequired')})}
                            disabled={isLoading}
                        />
                        {errors.firstName && <p className="text-destructive text-sm">{errors.firstName.message}</p>}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="lastName">{t('settings.lastName')} *</Label>
                        <Input
                            id="lastName"
                            {...register("lastName", {required: t('settings.lastNameRequired')})}
                            disabled={isLoading}
                        />
                        {errors.lastName && <p className="text-destructive text-sm">{errors.lastName.message}</p>}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="teamName">{t('settings.teamName')} *</Label>
                    <Input
                        id="teamName"
                        {...register("teamName", {required: t('settings.teamNameRequired')})}
                        disabled={isLoading}
                    />
                    {errors.teamName && <p className="text-destructive text-sm">{errors.teamName.message}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="streetAddress">{t('settings.streetAddress')} *</Label>
                    <Input
                        id="streetAddress"
                        {...register("streetAddress", {required: t('settings.streetAddressRequired')})}
                        disabled={isLoading}
                    />
                    {errors.streetAddress && <p className="text-destructive text-sm">{errors.streetAddress.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="city">{t('settings.city')} *</Label>
                        <Input
                            id="city"
                            {...register("city", {required: t('settings.cityRequired')})}
                            disabled={isLoading}
                        />
                        {errors.city && <p className="text-destructive text-sm">{errors.city.message}</p>}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="stateProvince">{t('settings.stateProvince')}</Label>
                        <Input
                            id="stateProvince"
                            {...register("stateProvince")}
                            disabled={isLoading}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="postalCode">{t('settings.postalCode')} *</Label>
                        <Input
                            id="postalCode"
                            {...register("postalCode", {required: t('settings.postalCodeRequired')})}
                            disabled={isLoading}
                        />
                        {errors.postalCode && <p className="text-destructive text-sm">{errors.postalCode.message}</p>}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="country">{t('settings.country')} *</Label>
                        <Input
                            id="country"
                            {...register("country", {required: t('settings.countryRequired')})}
                            disabled={isLoading}
                        />
                        {errors.country && <p className="text-destructive text-sm">{errors.country.message}</p>}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="phoneNumber">{t('settings.phoneNumber')} *</Label>
                    <Input
                        id="phoneNumber"
                        {...register("phoneNumber", {
                            required: t('settings.phoneNumberRequired'),
                            validate: {
                                notEmpty: (value) => value.trim().length > 0 || t('settings.phoneNumberRequired'),
                                validChars: (value) => /^[+()\d\s.-]+$/.test(value.trim()) || t('settings.phoneNumberRequired'),
                                minDigits: (value) => (value.match(/\d/g)?.length ?? 0) >= 6 || t('settings.phoneNumberRequired')
                            }
                        })}
                        disabled={isLoading}
                    />
                    {errors.phoneNumber && <p className="text-destructive text-sm">{errors.phoneNumber.message}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="billingEmail">{t('settings.billingEmail')} *</Label>
                    <Input id="billingEmail"
                           type="email" {...register("billingEmail", {required: t('settings.billingEmailRequired')})}
                           disabled={isLoading}/>
                    {errors.billingEmail && <p className="text-destructive text-sm">{errors.billingEmail.message}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="vatNumber">{t('settings.vatNumber')}{requireVat ? ' *' : ''}</Label>
                    <Input
                        id="vatNumber"
                        {...register("vatNumber", requireVat ? {required: t('settings.vatNumberRequired')} : undefined)}
                        disabled={isLoading}
                    />
                    {errors.vatNumber && <p className="text-destructive text-sm">{errors.vatNumber.message}</p>}
                </div>
            </form>
            {showSubmitButton && (
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="submit"
                        form="team-information-form"
                        disabled={!isValid || isSubmitting || isLoading}
                        className="rounded-full px-8"
                    >
                        {isSubmitting ? t('common.action.saving') : (submitButtonText || t('trial.startTrial'))}
                    </Button>
                </div>
            )}
        </>
    );
}
